import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Mail, Phone, Clock, LogOut, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ta_admin_pw";

export default function Admin() {
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async (password) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/contacts`, {
        headers: { "X-Admin-Password": password },
      });
      if (res.status === 401) {
        setAuthed(false);
        sessionStorage.removeItem(STORAGE_KEY);
        toast.error("Session expired. Please sign in again.");
        return;
      }
      const data = await res.json();
      setContacts(data.contacts || []);
      setUnread(data.unread || 0);
    } catch (e) {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      setAuthed(true);
      load(saved);
    }
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw }),
      });
      if (res.ok) {
        sessionStorage.setItem(STORAGE_KEY, pw);
        setAuthed(true);
        toast.success("Welcome back.");
        load(pw);
      } else {
        toast.error("Incorrect password.");
      }
    } catch (e) {
      toast.error("Network error.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPw("");
    setContacts([]);
  };

  const toggleRead = async (c) => {
    const password = sessionStorage.getItem(STORAGE_KEY);
    const next = !c.read;
    setContacts((list) => list.map((x) => (x.id === c.id ? { ...x, read: next } : x)));
    setUnread((u) => u + (next ? -1 : 1));
    try {
      await fetch(`${API}/admin/contacts/${c.id}/read`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-Admin-Password": password },
        body: JSON.stringify({ read: next }),
      });
    } catch (e) {
      toast.error("Could not update. Refreshing…");
      load(password);
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-AU", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-6">
        <div className="grain-overlay" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <div className="flex justify-center mb-12"><Logo markSize={44} testId="admin-logo" /></div>
          <div className="bg-[#12121a] border border-white/5 p-10">
            <div className="h-12 w-12 grid place-items-center border border-[#c9a84c]/30 rounded-full mb-8">
              <Lock size={20} className="text-[#c9a84c]" strokeWidth={1.5} />
            </div>
            <h1 className="font-display text-3xl text-[#f8f9fa] font-light tracking-tight mb-2">Admin Access</h1>
            <p className="font-body text-sm text-[#9ba1a6] mb-8">Enter the password to view enquiries.</p>
            <form onSubmit={login} data-testid="admin-login-form">
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Password"
                data-testid="admin-password-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[#c9a84c] py-3 font-body text-[#f8f9fa] placeholder:text-[#5c6066] outline-none transition-colors duration-300"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-button"
                className="mt-10 w-full font-body text-sm tracking-wide px-8 py-4 rounded-full bg-[#c9a84c] text-[#0a0a0f] font-medium hover:bg-[#e0c268] transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </div>
          <a href="/" data-testid="admin-back-home" className="block text-center mt-8 font-body text-xs tracking-[0.2em] uppercase text-[#9ba1a6] hover:text-[#c9a84c] transition-colors">
            ← Back to site
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="admin-dashboard">
      <div className="grain-overlay" />
      <header className="border-b border-white/5 bg-[#12121a]/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Logo testId="admin-header-logo" />
          <div className="flex items-center gap-3 md:gap-5">
            <button
              onClick={() => load(sessionStorage.getItem(STORAGE_KEY))}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 font-body text-sm text-[#9ba1a6] hover:text-[#e8d9a8] transition-colors"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 font-body text-sm text-[#9ba1a6] hover:text-[#e8d9a8] transition-colors"
            >
              <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-14">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-body text-xs tracking-[0.3em] uppercase text-[#c9a84c] mb-3">Enquiries</p>
            <h1 className="font-display text-4xl sm:text-5xl text-[#f8f9fa] font-light tracking-tight">Contact Submissions</h1>
          </div>
          <div className="flex gap-4">
            <div className="bg-[#12121a] border border-white/5 px-6 py-4 text-center">
              <p className="font-display text-3xl text-[#f8f9fa]" data-testid="admin-total-count">{contacts.length}</p>
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-[#9ba1a6] mt-1">Total</p>
            </div>
            <div className="bg-[#12121a] border border-[#c9a84c]/30 px-6 py-4 text-center">
              <p className="font-display text-3xl text-[#c9a84c]" data-testid="admin-unread-count">{unread}</p>
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-[#9ba1a6] mt-1">Unread</p>
            </div>
          </div>
        </div>

        {contacts.length === 0 ? (
          <div className="bg-[#12121a] border border-white/5 py-24 text-center" data-testid="admin-empty-state">
            <Inbox size={40} className="text-[#c9a84c]/50 mx-auto mb-6" strokeWidth={1.2} />
            <p className="font-display text-2xl text-[#f8f9fa] font-light mb-2">No enquiries yet</p>
            <p className="font-body text-sm text-[#9ba1a6]">New submissions from the website will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence initial={false}>
              {contacts.map((c) => (
                <motion.div
                  key={c.id}
                  layout
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="admin-contact-row"
                  className={`relative bg-[#12121a] border p-6 md:p-8 transition-colors duration-300 ${
                    c.read ? "border-white/5" : "border-[#c9a84c]/30"
                  }`}
                >
                  {!c.read && <span className="absolute top-6 left-0 w-1 h-8 bg-[#c9a84c]" />}
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-display text-xl text-[#f8f9fa] font-medium" data-testid="admin-contact-name">{c.name}</h3>
                        {!c.read && (
                          <span className="font-body text-[9px] tracking-[0.2em] uppercase text-[#0a0a0f] bg-[#c9a84c] px-2 py-0.5 rounded-full">New</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-3">
                        <a href={`mailto:${c.email}`} className="flex items-center gap-2 font-body text-sm text-[#9ba1a6] hover:text-[#c9a84c] transition-colors">
                          <Mail size={13} /> {c.email}
                        </a>
                        {c.phone ? (
                          <a href={`tel:${c.phone}`} className="flex items-center gap-2 font-body text-sm text-[#9ba1a6] hover:text-[#c9a84c] transition-colors">
                            <Phone size={13} /> {c.phone}
                          </a>
                        ) : null}
                        <span className="flex items-center gap-2 font-body text-sm text-[#6a6f75]">
                          <Clock size={13} /> {fmt(c.created_at)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleRead(c)}
                      data-testid="admin-toggle-read"
                      className={`shrink-0 font-body text-xs tracking-wide px-4 py-2 rounded-full border transition-colors duration-300 ${
                        c.read
                          ? "border-white/10 text-[#9ba1a6] hover:border-[#c9a84c]/40 hover:text-[#e8d9a8]"
                          : "border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c] hover:text-[#0a0a0f]"
                      }`}
                    >
                      {c.read ? "Mark unread" : "Mark as read"}
                    </button>
                  </div>
                  <p className="font-body text-[#c9c9cf] font-light leading-relaxed whitespace-pre-wrap border-t border-white/5 pt-4" data-testid="admin-contact-message">
                    {c.message}
                  </p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
}
